import {
  formatMembershipProfileValue,
  type ParticipantSummary,
  type SurveyParticipantSnapshot,
  type SurveyResponse,
} from '@citizen-walk/shared-types';

/**
 * 回答に記録された回答者情報 (participantSnapshot) の空欄を、参加者の今の登録内容で補う (純粋関数)。
 *
 * アプリは 2026-10-09 の修正まで、回答者情報としてニックネーム (それも端末内の保存値。無いと
 * `anon-<uid 先頭6>`) しか記録していなかった。記録済みの回答を書き換えずに個票・CSV・JSON で
 * 回答者が分かるよう、記録が空 (ニックネームは `anon-` で始まる仮の名前も空とみなす) の項目だけ、
 * 同じ uid の参加登録 (無ければ同一人物の代表端末) の値で埋める。記録がある項目は記録を優先する。
 *
 * @param personIdByUid uid → 同一人物の代表端末 uid (useSurveyPersonIds)。
 */
export function fillRespondentProfiles(
  responses: SurveyResponse[],
  participantByUid: Map<string, ParticipantSummary>,
  personIdByUid: Map<string, string>,
  sessions: Array<{ id: string; name: string; code: string }>,
): SurveyResponse[] {
  if (participantByUid.size === 0) return responses;
  const sessionById = new Map(sessions.map((s) => [s.id, s]));
  return responses.map((r) => {
    const p =
      participantByUid.get(r.participantUid) ??
      participantByUid.get(personIdByUid.get(r.participantUid) ?? '');
    if (!p) return r;
    const snap = r.participantSnapshot;
    const text = (recorded: string | null, current: unknown): string | null =>
      recorded && recorded.trim() !== '' ? recorded : formatMembershipProfileValue(current) || null;
    const recordedNickname = snap.nickname?.trim() ?? '';
    const filled: SurveyParticipantSnapshot = {
      nickname:
        recordedNickname !== '' && !recordedNickname.startsWith('anon-')
          ? recordedNickname
          : p.nickname || recordedNickname,
      ageGroup: text(snap.ageGroup, p.ageGroup),
      gender: text(snap.gender, p.gender),
      relationship: text(snap.relationship, p.relationship),
      attribute: text(snap.attribute, p.attribute),
      joinedSessions:
        (snap.joinedSessions ?? []).length > 0
          ? snap.joinedSessions
          : p.joinedSessionIds.map((sid) => {
              const s = sessionById.get(sid);
              return { sessionId: sid, name: s?.name ?? sid, code: s?.code ?? '' };
            }),
    };
    return { ...r, participantSnapshot: filled };
  });
}
